import React, { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAppState } from '../context/AppStateContext';
import { useApi } from '../hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import StudentInfo from './StudentInfo';
import CourseList from './CourseList';
import Calendar from './student/Calendar';
import RecentActivities from './student/RecentActivities';
import CompareControls from './dashboard/CompareControls';
import { calculate8Level, calculateMeanScore } from '../utils/cbcUtils';
import { ArrowDownIcon, ArrowUpIcon, AcademicCapIcon, BookOpenIcon, ClockIcon } from '@heroicons/react/24/outline';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const StatCard = ({ label, value, delta, icon: Icon, trend }) => (
    <div className="bg-white rounded-2xl md:rounded-3xl shadow-sm border border-slate-100 p-3 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5 hover:shadow-md transition-all group h-full">
        <div className="h-10 w-10 md:h-14 md:w-14 rounded-xl md:rounded-2xl bg-[#18216D]/5 text-[#18216D] flex items-center justify-center group-hover:bg-[#18216D] group-hover:text-white transition-all shadow-sm shrink-0">
            <Icon className="h-5 w-5 md:h-7 md:w-7" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-[10px] md:text-xs font-semibold uppercase tracking-wide text-gray-400 truncate">{label}</p>
            <p className="text-lg md:text-2xl font-black text-[#18216D] truncate">{value}</p>
            {delta && (
                <div className="flex items-center text-[10px] md:text-sm mt-0.5 md:mt-1 truncate">
                    {trend === 'up' ? (
                        <ArrowUpIcon className="h-3 w-3 md:h-4 md:w-4 text-green-500 mr-1" />
                    ) : (
                        <ArrowDownIcon className="h-3 w-3 md:h-4 md:w-4 text-rose-500 mr-1" />
                    )}
                    <span className={trend === 'up' ? 'text-green-600' : 'text-rose-600'}>{delta}</span>
                </div>
            )}
        </div>
    </div>
);

const StudentDashboard = () => {
    const { user } = useAuth();
    const { get } = useApi();
    const { courses, performanceBands } = useAppState();
    const [comparisonFilters, setComparisonFilters] = useState({});
    const { data: comparisonData = [], isLoading: compLoading } = useQuery({
        queryKey: ['comparison-performance', user?.id, comparisonFilters],
        queryFn: async () => {
            if (!user?.id) return [];
            const queryParams = new URLSearchParams(comparisonFilters).toString();
            return get(`/api/cbc/competency-assessments/performance-comparison/${user.id}/${queryParams ? `?${queryParams}` : ''}`);
        },
        enabled: !!user?.id,
        staleTime: 10 * 60 * 1000, // 10 minutes cache
    });

    const fullName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Guest' : 'Guest';

    const overview = useMemo(() => {
        const totalCourses = Array.isArray(courses) ? courses.length : 0;
        let totalAssignments = 0;
        let completedAssignments = 0;
        let totalQuizzes = 0;
        let completedQuizzes = 0;

        if (Array.isArray(courses)) {
            courses.forEach(course => {
                // Assignments
                const courseAssignments = course.assignments || [];
                totalAssignments += courseAssignments.length;

                const subs = course.student_submissions?.assignments || [];
                const uniqueAssignmentIds = new Set(subs.filter(s => s.status === 'submitted' || s.status === 'graded').map(s => String(s.assignment)));
                completedAssignments += uniqueAssignmentIds.size;

                // Quizzes
                const courseQuizzes = course.quizzes || [];
                totalQuizzes += courseQuizzes.length;

                const quizSubs = course.student_submissions?.quizzes || [];
                const uniqueQuizIds = new Set(
                    quizSubs
                        .filter(s => {
                            const status = (s.status || '').toLowerCase();
                            return status === 'graded' || status === 'auto_generated' || status === 'completed';
                        })
                        .map(s => String(s.quiz))
                );
                completedQuizzes += uniqueQuizIds.size;
            });
        }

        const allAssignments = Array.isArray(courses) ? courses.flatMap(c => (c.assignments || []).map(a => ({ ...a, courseName: c.name }))) : [];
        const allQuizzes = Array.isArray(courses) ? courses.flatMap(c => (c.quizzes || []).map(q => ({ ...q, courseName: c.name, type: 'Quiz' }))) : [];
        const allEvents = [...allAssignments, ...allQuizzes];

        return {
            totalCourses,
            totalAssignments,
            completedAssignments,
            totalQuizzes,
            completedQuizzes,
            allAssignments,
            allEvents
        };
    }, [courses]);

    return (
        <div className="min-h-screen bg-slate-50">
            <main className="max-w-7xl mx-auto py-6 md:py-10 px-4 sm:px-6 lg:px-8 space-y-6 md:space-y-12">
                <header className="space-y-3 relative overflow-hidden bg-[#18216D] p-8 md:p-12 rounded-[2.5rem] shadow-2xl shadow-indigo-900/20 text-white">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                    <div className="relative z-10">
                        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#FFC425] mb-2">Scholar Portal</p>
                        <h1 className="text-3xl md:text-6xl font-black tracking-tighter italic">Welcome, {fullName}</h1>
                        <p className="text-indigo-100/70 max-w-2xl font-medium text-sm md:text-lg mt-4 md:mt-6">
                            View your learning areas, track assignments, and monitor your progress.
                        </p>
                    </div>
                </header>

                <section className="grid grid-cols-3 gap-3 md:gap-6">
                    <div className="w-full">
                        <StatCard
                            label="Learning Areas"
                            value={overview.totalCourses}
                            delta="Active"
                            trend="up"
                            icon={BookOpenIcon}
                        />
                    </div>
                    <div className="w-full">
                        <StatCard
                            label="Assessments"
                            value={overview.totalAssignments}
                            delta={`${overview.completedAssignments} Done`}
                            trend="up"
                            icon={AcademicCapIcon}
                        />
                    </div>
                    <div className="w-full">
                        <StatCard
                            label="Quizzes"
                            value={overview.totalQuizzes}
                            delta={`${overview.completedQuizzes} Done`}
                            trend="up"
                            icon={ClockIcon}
                        />
                    </div>
                </section>


                <section className="grid grid-cols-1 lg:grid-cols-10 gap-8">
                    <div className="hidden lg:block lg:col-span-3">
                        <StudentInfo student={user} />
                    </div>
                    <div className="lg:col-span-10 lg:hidden block">
                        <CourseList />
                    </div>
                    <div className="hidden lg:block lg:col-span-7">
                        <CourseList />
                    </div>
                </section>

                <section className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-sm border border-slate-100">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                        <div>
                            <h2 className="text-2xl font-black text-[#18216D] tracking-tight">Performance Comparison</h2>
                            <p className="text-slate-400 font-bold text-sm mt-1 truncate">Comparing Academic Mastery Across Periods</p>
                        </div>
                        <div className="flex items-center gap-4">
                            <CompareControls onFilterChange={setComparisonFilters} />
                        </div>
                    </div>
                    <div className="h-[400px] w-full">
                        {compLoading ? (
                            <div className="h-full w-full flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18216D]"></div>
                            </div>
                        ) : comparisonData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={comparisonData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis
                                        dataKey="learning_area"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
                                        angle={-45}
                                        textAnchor="end"
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }}
                                        domain={[0, 100]}
                                    />
                                    <Tooltip
                                        content={({ active, payload, label }) => {
                                            if (active && payload && payload.length) {
                                                const current = payload.find(p => p.dataKey === 'current_term');
                                                const previous = payload.find(p => p.dataKey === 'previous_term');
                                                return (
                                                    <div className="bg-white p-2.5 rounded-xl shadow-2xl border border-slate-100 min-w-[140px] pointer-events-none">
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-50 pb-1 italic truncate">{label}</p>
                                                        <div className="space-y-2.5">
                                                            {current && (
                                                                <div className="flex flex-col">
                                                                    <div className="flex items-baseline gap-1">
                                                                        <span className="text-base font-black text-[#18216D] italic tracking-tight uppercase leading-none">{calculate8Level(current.value, performanceBands)}</span>
                                                                        <span className="text-[8px] font-bold text-[#18216D] opacity-50 leading-none">{calculateMeanScore(current.value, performanceBands).toFixed(2)} MS</span>
                                                                    </div>
                                                                    <p className="text-[11px] font-black text-[#18216D] mt-0.5 tracking-tight">{current.value}%</p>
                                                                </div>
                                                            )}
                                                            {previous && (
                                                                <div className="flex flex-col pt-2 border-t border-slate-50">
                                                                    <div className="flex items-baseline gap-1">
                                                                        <span className="text-base font-black text-[#FFC425] italic tracking-tight uppercase leading-none">{calculate8Level(previous.value, performanceBands)}</span>
                                                                        <span className="text-[8px] font-bold text-[#FFC425] opacity-50 leading-none">{calculateMeanScore(previous.value, performanceBands).toFixed(2)} MS</span>
                                                                    </div>
                                                                    <p className="text-[11px] font-black text-[#FFC425] mt-0.5 tracking-tight">{previous.value}%</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                    <Bar
                                        name={`Current: ${comparisonData[0]?.current_term_name || 'Term'}`}
                                        dataKey="current_term"
                                        fill="#18216D"
                                        radius={[4, 4, 0, 0]}
                                        barSize={30}
                                    />
                                    <Bar
                                        name={`Previous: ${comparisonData[0]?.previous_term_name || 'Term'}`}
                                        dataKey="previous_term"
                                        fill="#FFC425"
                                        radius={[4, 4, 0, 0]}
                                        barSize={30}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-full w-full flex items-center justify-center text-slate-400 font-bold italic">
                                Insufficient data for term comparison.
                            </div>
                        )}
                    </div>
                </section>

                <section className="grid grid-cols-1 lg:grid-cols-10 gap-8">
                    <div className="lg:col-span-6">
                        <Calendar events={overview.allEvents} />
                    </div>
                    <div className="lg:col-span-4">
                        <RecentActivities />
                    </div>
                </section>

            </main>
        </div>
    );
};

export default StudentDashboard;
